/**
 * Los grupos como árbol (ADR-0027). Lo mismo que hace el servidor con la tabla
 * de ancestros, pero sólo para mostrar: quién ve qué lo decide la base.
 */
interface GrupoEnArbol {
  id: string;
  name: string;
  parentId?: string;
}

/**
 * En orden de árbol: cada grupo seguido de su rama, hermanos por nombre. Un
 * grupo cuyo padre no está en la lista —inactivo, o fuera del filtro— se
 * muestra como raíz en vez de desaparecer.
 */
export function enArbol<G extends GrupoEnArbol>(grupos: readonly G[]): { grupo: G; nivel: number }[] {
  const ids = new Set(grupos.map((g) => g.id));
  const hijos = new Map<string | undefined, G[]>();
  for (const g of grupos) {
    const padre = g.parentId && ids.has(g.parentId) ? g.parentId : undefined;
    hijos.set(padre, [...(hijos.get(padre) ?? []), g]);
  }
  const salida: { grupo: G; nivel: number }[] = [];
  const recorrer = (padre: string | undefined, nivel: number) => {
    const ordenados = [...(hijos.get(padre) ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name, 'es-MX', { sensitivity: 'base' }),
    );
    for (const g of ordenados) {
      salida.push({ grupo: g, nivel });
      recorrer(g.id, nivel + 1);
    }
  };
  recorrer(undefined, 0);
  return salida;
}

/** El grupo y todos los que cuelgan de él: lo que no puede ser su padre. */
export function ramaDe(grupos: readonly GrupoEnArbol[], id: string): Set<string> {
  const rama = new Set([id]);
  let crecio = true;
  while (crecio) {
    crecio = false;
    for (const g of grupos) {
      if (g.parentId && rama.has(g.parentId) && !rama.has(g.id)) {
        rama.add(g.id);
        crecio = true;
      }
    }
  }
  return rama;
}

/** Los de arriba, del más cercano al más lejano. */
export function ancestrosDe(grupos: readonly GrupoEnArbol[], id: string): GrupoEnArbol[] {
  const porId = new Map(grupos.map((g) => [g.id, g]));
  const arriba: GrupoEnArbol[] = [];
  let actual = porId.get(id)?.parentId;
  while (actual && porId.has(actual) && !arriba.some((g) => g.id === actual)) {
    const padre = porId.get(actual)!;
    arriba.push(padre);
    actual = padre.parentId;
  }
  return arriba;
}

/** «Crédito › Analistas»: de dónde viene, para cuando se ve fuera del árbol. */
export function rutaDe(grupos: readonly GrupoEnArbol[], id: string): string {
  const grupo = grupos.find((g) => g.id === id);
  if (!grupo) return '';
  return [...ancestrosDe(grupos, id)].reverse().map((g) => g.name).concat(grupo.name).join(' › ');
}
